import { APIClient } from "../helpers/api_helper";
import { ApiResponse } from "../types/api";
import { ClientRecordTemplateSchema } from "./clientRecordTemplateService";

const api = new APIClient();

const resource = (token: string) => `/public/appointments/${encodeURIComponent(token)}`;

export interface PublicAppointmentManageActions {
  can_confirm: boolean;
  can_cancel: boolean;
  can_preattend?: boolean;
  reschedule_url: string | null;
  talk_to_salon_url: string | null;
  calendar_url: string | null;
}

export interface PublicPreattendSummary {
  needs_health_record: boolean;
  needs_consent: boolean;
  can_fill_health_record: boolean;
  can_sign_consent: boolean;
  preattend_complete: boolean;
}

export interface PublicAppointmentManage {
  id: number;
  status: string;
  start_datetime: string;
  end_datetime: string;
  schedule_label: string;
  tenant: { name: string | null };
  service: { id: number; name: string } | null;
  professional: { id: number; name: string; color_hex?: string } | null;
  client: { name: string } | null;
  actions: PublicAppointmentManageActions;
  preattend?: PublicPreattendSummary;
}

export interface PublicPreattendForm {
  needs_health_record: boolean;
  needs_consent: boolean;
  requires_consent_blocks: boolean;
  has_signed_consent: boolean;
  preattend_complete: boolean;
  anamnesis_scope: string;
  service_id: number | null;
  template: {
    id: number;
    title: string;
    schema_json: ClientRecordTemplateSchema;
  } | null;
  template_unavailable: boolean;
  record_id: number | null;
  answers_json: Record<string, unknown>;
  consent_text: string;
  requires_signature: boolean;
  appointment: {
    id: number;
    schedule_label: string;
    service: { id: number; name: string } | null;
  };
  tenant: { name: string | null };
  client: { first_name: string } | null;
}

export const getPublicAppointment = (
  token: string
): Promise<ApiResponse<PublicAppointmentManage>> =>
  api.get<ApiResponse<PublicAppointmentManage>>(resource(token));

export const confirmPublicAppointment = (
  token: string
): Promise<ApiResponse<PublicAppointmentManage>> =>
  api.create<ApiResponse<PublicAppointmentManage>>(`${resource(token)}/confirm`, {});

export const cancelPublicAppointment = (
  token: string
): Promise<ApiResponse<PublicAppointmentManage>> =>
  api.create<ApiResponse<PublicAppointmentManage>>(`${resource(token)}/cancel`, {});

export const getPublicPreattend = (
  token: string
): Promise<ApiResponse<PublicPreattendForm>> =>
  api.get<ApiResponse<PublicPreattendForm>>(`${resource(token)}/preattend`);

export const submitPublicPreattendAnswers = (
  token: string,
  answers_json: Record<string, unknown>
): Promise<ApiResponse<PublicPreattendForm>> =>
  api.create<ApiResponse<PublicPreattendForm>>(`${resource(token)}/preattend/answers`, {
    answers_json,
  });

export const submitPublicPreattendConsent = (
  token: string,
  payload: {
    answers_json?: Record<string, unknown>;
    consent_text_snapshot?: string;
    signature_image_base64?: string | null;
  }
): Promise<ApiResponse<PublicPreattendForm>> =>
  api.create<ApiResponse<PublicPreattendForm>>(`${resource(token)}/preattend/consent`, payload);
